import { css } from '@emotion/react';
import { Traveler } from '@flysas-tech/book-value-stream-models';
import { Avatar, BREAKPOINTS, Button, Dialog, Paragraph, semanticTokens, tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { useRef, useState } from 'react';
import { calculateTravelerTypeBasedOnBirthDate, cleanPhoneNumber, isTravelerEdited, SavedTraveler } from 'shared';

import { useMediaQuery } from '../../hooks';

const styles = {
  savedTravelers: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${tokens.dimension['space-6']};
  `,
  savedTravelerButton: css`
    all: unset;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;

    &:focus-visible {
      outline: 2px solid ${semanticTokens.color.border.focus};
    }
  `,
  noPadding: css`
    padding: 0;
  `,
};

type Props = {
  travelDate: string;
  traveler: Traveler;
  savedTravelers: SavedTraveler[];
  updateTraveler: (id: string, traveler: Partial<Traveler>) => void;
};

export const SavedTravelers = ({ travelDate, traveler, savedTravelers, updateTraveler }: Props) => {
  const [open, setOpen] = useState(false);

  const isMobile = useMediaQuery({ max: BREAKPOINTS.layout.sm });

  const isValidRef = useRef(isTravelerEdited(traveler));

  const eligibleTravelers = savedTravelers.filter(savedTraveler => {
    if (savedTraveler.firstName === traveler.firstName && savedTraveler.lastName === traveler.lastName) return false;
    if (!savedTraveler.dateOfBirth) return true;
    const savedTravelerType = calculateTravelerTypeBasedOnBirthDate(savedTraveler.dateOfBirth, travelDate);
    return savedTravelerType === traveler.type;
  });

  if (eligibleTravelers.length === 0) {
    return null;
  }

  const handleAddSavedTraveler = (savedTraveler: SavedTraveler) => {
    const isPrimary = traveler.isPrimary;

    updateTraveler(traveler.id, {
      firstName: savedTraveler.firstName,
      lastName: savedTraveler.lastName,
      gender: savedTraveler.gender,
      dateOfBirth: savedTraveler.dateOfBirth,
      email: isPrimary ? savedTraveler.email : undefined,
      phoneNumber: isPrimary ? cleanPhoneNumber(savedTraveler.phoneNumber) : undefined,
      frequentTraveler: savedTraveler.frequentTraveler,
    });

    setOpen(false);
  };

  const buttonLabel = isValidRef.current
    ? t('travelers.changeTraveler.buttonLabel', { defaultValue: 'Change traveler' })
    : t('travelers.selectTraveler.buttonLabel', { defaultValue: 'Select traveler' });

  return (
    <>
      <Button
        variant="link"
        leadingIcon="People"
        onClick={() => setOpen(true)}
        size={isMobile ? 'small' : 'medium'}
        css={styles.noPadding}
      >
        {isMobile ? '' : buttonLabel}
      </Button>

      {open && (
        <Dialog.Root>
          <Dialog.Window
            title={t('travelers.chooseSavedTraveler', { defaultValue: 'Choose saved traveler' })}
            onClose={() => setOpen(false)}
            onCloseLabel="Close"
          >
            <Dialog.Container>
              <div css={styles.savedTravelers}>
                {eligibleTravelers?.map(traveler => (
                  <button
                    key={traveler.id}
                    css={styles.savedTravelerButton}
                    onClick={() => handleAddSavedTraveler(traveler)}
                  >
                    <Avatar name={`${traveler.firstName} ${traveler.lastName}`} size="medium" />
                    <Paragraph color="primary" size={'paragraph-2'}>
                      {traveler.firstName}
                    </Paragraph>
                  </button>
                ))}
              </div>
            </Dialog.Container>
          </Dialog.Window>
        </Dialog.Root>
      )}
    </>
  );
};
